import React, { useMemo, useState, useEffect, useContext } from 'react';
import { Clock, FileText, Globe, KeyRound, LayoutGrid, Link as LinkIcon, Pencil, Settings2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InvestorRelationshipSections } from './overview/InvestorRelationshipSections';
import { InvestorPolicySections } from './overview/InvestorPolicySections';
import { DocumentsTab } from './DocumentsTab';
import { CredentialsTab } from './CredentialsTab';
import { InvestorTemplatesTab } from '../form/InvestorTemplatesTab';
import { LinkEncompassTemplateDialog } from '../LinkEncompassTemplateDialog';
import { LW_THEME_CLASS } from '../shared/InvestorThemeScope';
import { generateRecentActivity, getActivityIcon, getActivityColor, formatTimeAgo } from '@/utils/investor/activityUtils';
import type { Investor } from '@/types/investor';
import type { InvestorEncompassTemplate } from '@/types/investor-encompass';
import { InvestorService } from '@/services/investorService';
import { investorEncompassTemplateService } from '@/services/investorEncompassTemplateService';
import { formatPhoneNumber, formatUrl, getOptionalNumberError, isTenDigitPhone, isValidEmail, isValidUrl } from '@/utils/investor/validation';
import { getRuleValueError } from '@/utils/investor/businessRules';
import { createComponentLogger } from '@/utils/logger';
import { AuthContext } from '@/contexts/authContextDefinition';

interface InvestorSlideOverProps {
  investor: Investor | null;
  open: boolean;
  organizationId: string;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
  onInvestorUpdated: (updated: Investor) => void;
  editOnOpen?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

const stripProtocol = (url?: string) => (url || '').replace(/^https?:\/\//i, '').replace(/\/$/, '');
const getInvestorAvatarLabel = (name: string) => name.trim().charAt(0).toUpperCase() || '?';
const getInvestorLogoColor = (investor: Investor) => (investor as Investor & { logoColor?: string }).logoColor;

const formatHeaderTimeAgo = (timestamp: string): string => {
  const date = new Date(timestamp);
  const days = Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
  if (days === 0) {
    return 'Today';
  }
  if (days === 1) {
    return 'Yesterday';
  }
  if (days < 7) {
    return `${days} days ago`;
  }
  if (days < 30) {
    return `${Math.floor(days / 7)} weeks ago`;
  }
  if (days < 365) {
    return `${Math.floor(days / 30)} months ago`;
  }
  return `${Math.floor(days / 365)} years ago`;
};

const getNetWorthRowsForDisplay = (investor: Investor) =>
  investor.netWorthRequirementRows?.length
    ? investor.netWorthRequirementRows
    : [
      ...(typeof investor.netWorthRequirements?.delegated === 'number' ? [{ label: 'Delegated', amount: investor.netWorthRequirements.delegated, notes: '' }] : []),
      ...(typeof investor.netWorthRequirements?.nonDelegated === 'number' ? [{ label: 'Non-delegated', amount: investor.netWorthRequirements.nonDelegated, notes: '' }] : [])
    ];

const buildOverviewDraft = (investor: Investor): Partial<Investor> => ({
  investor: investor.investor,
  canDoWholesale: investor.canDoWholesale,
  canGoCorrespondent: investor.canGoCorrespondent,
  status: investor.status,
  approvedAsOfDate: investor.approvedAsOfDate,
  correspondentStatus: investor.correspondentStatus,
  correspondentApprovedAsOfDate: investor.correspondentApprovedAsOfDate,
  delegatedType: investor.delegatedType,
  delegation: investor.delegation ?? { delegated: [], nonDelegated: [] },
  netWorthRequirements: investor.netWorthRequirements ?? {},
  netWorthRequirementRows: getNetWorthRowsForDisplay(investor),
  wholesaleProducts: investor.wholesaleProducts ?? [],
  correspondentProducts: investor.correspondentProducts ?? [],
  wholesaleLenderPaidComp: investor.wholesaleLenderPaidComp ?? null,
  wholesaleAdditionalLendwiseFee: investor.wholesaleAdditionalLendwiseFee ?? null,
  website: investor.website ?? '',
  wholesaleCanIntegrateOptimalBlue: investor.wholesaleCanIntegrateOptimalBlue ?? false,
  wholesaleHasIntegratedOptimalBlue: investor.wholesaleHasIntegratedOptimalBlue ?? false,
  correspondentCanIntegrateOptimalBlue: investor.correspondentCanIntegrateOptimalBlue ?? false,
  correspondentHasIntegratedOptimalBlue: investor.correspondentHasIntegratedOptimalBlue ?? false,
  wholesaleEpoRule: investor.wholesaleEpoRule,
  wholesaleEpdRule: investor.wholesaleEpdRule,
  correspondentEpoRule: investor.correspondentEpoRule,
  correspondentEpdRule: investor.correspondentEpdRule,
  wholesaleAeName: investor.wholesaleAeName ?? '',
  wholesaleAePhone: investor.wholesaleAePhone ?? '',
  wholesaleAeEmail: investor.wholesaleAeEmail ?? '',
  correspondentAeName: investor.correspondentAeName ?? '',
  correspondentAePhone: investor.correspondentAePhone ?? '',
  correspondentAeEmail: investor.correspondentAeEmail ?? '',
  clarifiRelianceRequired: investor.clarifiRelianceRequired ?? false,
  clarifiRelianceInvestorRecipients: investor.clarifiRelianceInvestorRecipients ?? []
});

const log = createComponentLogger('InvestorSlideOver', 'ui');

export const InvestorSlideOver: React.FC<InvestorSlideOverProps> = ({
  investor, open, organizationId, canManage, onOpenChange, onInvestorUpdated, editOnOpen = false, onDirtyChange
}) => {
  const auth = useContext(AuthContext);
  const user = auth?.user;
  const isStrictAdmin = useMemo(
    () => (((user as { roles?: string[] } | null)?.roles ?? []) as string[]).map((role) => role.toLowerCase()).includes('admin'),
    [user]
  );
  const [servicerSelectionMode, setServicerSelectionMode] = useState<'product_scope' | 'per_loan'>(investor?.servicerSelectionMode ?? 'product_scope');
  useEffect(() => setServicerSelectionMode(investor?.servicerSelectionMode ?? 'product_scope'), [investor?.id, investor?.servicerSelectionMode]);
  const [tab, setTab] = useState('overview');
  // Unfinished document uploads count as unsaved work for investor switching.
  const [uploadPending, setUploadPending] = useState(false);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [savingOverview, setSavingOverview] = useState(false);
  const [draft, setDraft] = useState<Partial<Investor>>({});
  const [linkedTemplates, setLinkedTemplates] = useState<InvestorEncompassTemplate[]>([]);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [unlinkingTemplateIds, setUnlinkingTemplateIds] = useState<Set<string>>(new Set());
  const [syncingTemplateId, setSyncingTemplateId] = useState<string | null>(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const investorId = investor?.id;

  // Reset to Overview only when a different investor opens. Data refreshes below
  // must not kick users off Processing/Closing after a save.
  useEffect(() => {
    if (!open || !investorId) {
      return;
    }
    setTab('overview');
    setEditingSection(editOnOpen ? 'status' : null);
  }, [open, investorId, editOnOpen]);

  useEffect(() => {
    if (investor) {
      setDraft(buildOverviewDraft(investor));
    }
  }, [investor]);

  useEffect(() => {
    if (!open || !investor?.id || tab !== 'templates') {
      setLinkedTemplates([]);
      return;
    }

    let cancelled = false;
    setIsLoadingTemplates(true);
    investorEncompassTemplateService.getTemplatesByInvestorId(investor.id)
      .then((templates) => {
        if (!cancelled) {
          setLinkedTemplates(templates);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          log.error('Failed to load Encompass templates', { investorId: investor.id }, err as Error);
          toast.error('Failed to load Encompass templates');
          setLinkedTemplates([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingTemplates(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, investor?.id, tab]);

  const activities = useMemo(() => (investor ? generateRecentActivity([investor]) : []), [investor]);

  const overviewBaseline = useMemo(() => ({
    investor: investor?.investor ?? '',
    canDoWholesale: investor?.canDoWholesale ?? false,
    canGoCorrespondent: investor?.canGoCorrespondent ?? false,
    status: investor?.status ?? 'Inactive',
    correspondentStatus: investor?.correspondentStatus ?? 'Inactive',
    approvedAsOfDate: investor?.approvedAsOfDate,
    correspondentApprovedAsOfDate: investor?.correspondentApprovedAsOfDate,
    delegatedType: investor?.delegatedType,
    delegation: investor?.delegation ?? { delegated: [], nonDelegated: [] },
    netWorthRequirements: investor?.netWorthRequirements ?? {},
    netWorthRequirementRows: investor?.netWorthRequirementRows ?? [],
    wholesaleProducts: investor?.wholesaleProducts ?? [],
    correspondentProducts: investor?.correspondentProducts ?? [],
    wholesaleLenderPaidComp: investor?.wholesaleLenderPaidComp ?? null,
    wholesaleAdditionalLendwiseFee: investor?.wholesaleAdditionalLendwiseFee ?? null,
    website: investor?.website ?? '',
    wholesaleCanIntegrateOptimalBlue: investor?.wholesaleCanIntegrateOptimalBlue ?? false,
    wholesaleHasIntegratedOptimalBlue: investor?.wholesaleHasIntegratedOptimalBlue ?? false,
    correspondentCanIntegrateOptimalBlue: investor?.correspondentCanIntegrateOptimalBlue ?? false,
    correspondentHasIntegratedOptimalBlue: investor?.correspondentHasIntegratedOptimalBlue ?? false,
    wholesaleEpoRule: investor?.wholesaleEpoRule,
    wholesaleEpdRule: investor?.wholesaleEpdRule,
    correspondentEpoRule: investor?.correspondentEpoRule,
    correspondentEpdRule: investor?.correspondentEpdRule,
    wholesaleAeName: investor?.wholesaleAeName ?? '',
    wholesaleAePhone: investor?.wholesaleAePhone ?? '',
    wholesaleAeEmail: investor?.wholesaleAeEmail ?? '',
    correspondentAeName: investor?.correspondentAeName ?? '',
    correspondentAePhone: investor?.correspondentAePhone ?? '',
    correspondentAeEmail: investor?.correspondentAeEmail ?? '',
    clarifiRelianceRequired: investor?.clarifiRelianceRequired ?? false,
    clarifiRelianceInvestorRecipients: investor?.clarifiRelianceInvestorRecipients ?? []
  }), [investor]);

  const overviewDirty = Boolean(editingSection) && JSON.stringify(draft) !== JSON.stringify(overviewBaseline);

  useEffect(() => {
    onDirtyChange?.(overviewDirty || uploadPending);
  }, [overviewDirty, uploadPending, onDirtyChange]);

  if (!investor) {
    return null;
  }

  const hasWholesale = draft.canDoWholesale ?? investor.canDoWholesale;
  const hasCorrespondent = draft.canGoCorrespondent ?? investor.canGoCorrespondent;
  const correspondentApproved = hasCorrespondent && (draft.correspondentStatus ?? investor.correspondentStatus) === 'Approved';
  const netWorthRows = getNetWorthRowsForDisplay(investor);
  const logoColor = getInvestorLogoColor(investor);

  const handleOpenChange = (nextOpen: boolean) => {
    // eslint-disable-next-line no-alert
    if (!nextOpen && overviewDirty && !window.confirm('Discard unsaved investor detail changes?')) {
      return;
    }
    if (!nextOpen) {
      setEditingSection(null);
    }
    onOpenChange(nextOpen);
  };

  const updateDraft = <K extends keyof Investor>(key: K, value: Investor[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const resetDraft = () => {
    setDraft(buildOverviewDraft(investor));
  };

  const cancelSectionEdit = () => {
    resetDraft();
    setEditingSection(null);
  };

  const saveSectionEdit = async (keys: Array<keyof Investor>) => {
    const normalizedDraft: Partial<Investor> = { ...draft };

    if (keys.includes('investor') && !String(normalizedDraft.investor ?? '').trim()) {
      toast.error('Investor name is required');
      return;
    }

    if (
      (keys.includes('canDoWholesale') || keys.includes('canGoCorrespondent')) &&
      !normalizedDraft.canDoWholesale &&
      !normalizedDraft.canGoCorrespondent
    ) {
      toast.error('At least one channel must be enabled');
      return;
    }

    if (keys.includes('website')) {
      const rawWebsite = String(normalizedDraft.website ?? '').trim();
      const website = rawWebsite ? formatUrl(rawWebsite) : '';
      if (website && !isValidUrl(website)) {
        toast.error('Enter a valid website URL');
        return;
      }
      normalizedDraft.website = website;
    }

    for (const key of ['wholesaleAeEmail', 'correspondentAeEmail', 'aeEmail'] as Array<keyof Investor>) {
      const value = normalizedDraft[key];
      if (keys.includes(key) && typeof value === 'string' && value.trim() && !isValidEmail(value.trim())) {
        toast.error('Enter a valid AE email address');
        return;
      }
    }

    for (const key of ['wholesaleAePhone', 'correspondentAePhone', 'aePhone'] as Array<keyof Investor>) {
      const value = normalizedDraft[key];
      if (keys.includes(key) && typeof value === 'string' && value.trim()) {
        if (!isTenDigitPhone(value)) {
          toast.error('Enter a 10-digit AE phone number');
          return;
        }
        (normalizedDraft as Record<string, unknown>)[key] = formatPhoneNumber(value);
      }
    }

    const numericChecks: Array<{ key: keyof Investor; label: string; min?: number; max?: number }> = [
      // Matches the DB CHECK investors_wholesale_comp_range (1.00–2.50 or blank).
      { key: 'wholesaleLenderPaidComp', label: 'LPC %', min: 1, max: 2.5 },
      { key: 'wholesaleAdditionalLendwiseFee', label: 'Additional Lender Fee' }
    ];
    for (const { key, label, min, max } of numericChecks) {
      if (keys.includes(key)) {
        const error = getOptionalNumberError(normalizedDraft[key], label, { min: min ?? 0, max });
        if (error) {
          toast.error(error);
          return;
        }
      }
    }

    // EPO/EPD rules: a present value must be a positive integer (<=12 for payments);
    // blank/0 is allowed (clears the rule). Skips untouched siblings — convertFromDatabase
    // defaults a null rule to { value: 0 }, which must NOT block saving the other rule.
    const ruleChecks: Array<{ key: keyof Investor; label: string; channel: 'wholesale' | 'correspondent'; kind: 'epo' | 'epd' }> = [
      { key: 'wholesaleEpoRule', label: 'Wholesale EPO', channel: 'wholesale', kind: 'epo' },
      { key: 'wholesaleEpdRule', label: 'Wholesale EPD', channel: 'wholesale', kind: 'epd' },
      { key: 'correspondentEpoRule', label: 'Correspondent EPO', channel: 'correspondent', kind: 'epo' },
      { key: 'correspondentEpdRule', label: 'Correspondent EPD', channel: 'correspondent', kind: 'epd' }
    ];
    for (const { key, label, channel, kind } of ruleChecks) {
      if (keys.includes(key)) {
        const error = getRuleValueError(label, normalizedDraft[key] as { type?: string | null; value?: number | string | null } | undefined, channel, kind);
        if (error) {
          toast.error(error);
          return;
        }
      }
    }

    // Clarifii reliance recipients: when required, trim/filter the textarea lines and
    // require at least one valid investor-specific email. The live sender
    // (send-clarifii-reliance) matches on these and returns NO_CLARIFII_RECIPIENTS
    // if the list is empty — so "Required" with no recipients would be unsendable.
    // When not required, clear the list.
    if (keys.includes('clarifiRelianceRequired') || keys.includes('clarifiRelianceInvestorRecipients')) {
      if (normalizedDraft.clarifiRelianceRequired) {
        const recipients = (normalizedDraft.clarifiRelianceInvestorRecipients ?? [])
          .map((email) => email.trim())
          .filter((email) => email.length > 0);
        if (recipients.length === 0) {
          toast.error('Add at least one Clarifii recipient email when reliance is required');
          return;
        }
        const invalid = recipients.find((email) => !isValidEmail(email));
        if (invalid) {
          toast.error('Enter valid Clarifii recipient emails (one per line)');
          return;
        }
        normalizedDraft.clarifiRelianceInvestorRecipients = recipients;
      } else {
        normalizedDraft.clarifiRelianceInvestorRecipients = [];
      }
    }

    if (keys.includes('netWorthRequirementRows')) {
      const rows = (normalizedDraft.netWorthRequirementRows ?? []).map((row) => ({
        label: row.label?.trim() || undefined,
        amount: typeof row.amount === 'number' && Number.isFinite(row.amount) ? row.amount : null,
        notes: row.notes?.trim() || undefined
      })).filter((row) => row.label || row.amount !== null || row.notes);
      const invalidRow = rows.find((row) => row.amount !== null && row.amount < 0);
      if (invalidRow) {
        toast.error('Enter a valid net worth amount');
        return;
      }
      normalizedDraft.netWorthRequirementRows = rows;
    }

    if (
      keys.includes('status') &&
      normalizedDraft.canDoWholesale &&
      normalizedDraft.status === 'Approved' &&
      !normalizedDraft.approvedAsOfDate
    ) {
      toast.error('Wholesale approved date is required');
      return;
    }

    if (
      keys.includes('correspondentStatus') &&
      normalizedDraft.canGoCorrespondent &&
      normalizedDraft.correspondentStatus === 'Approved' &&
      !normalizedDraft.correspondentApprovedAsOfDate
    ) {
      toast.error('Correspondent approved date is required');
      return;
    }

    setSavingOverview(true);
    try {
      const patch: Partial<Investor> = {};
      keys.forEach((key) => {
        if (JSON.stringify(normalizedDraft[key]) !== JSON.stringify(overviewBaseline[key as keyof typeof overviewBaseline])) {
          (patch as Record<string, unknown>)[key] = normalizedDraft[key];
        }
      });
      if (Object.keys(patch).length === 0) {
        setEditingSection(null);
        return;
      }
      const updated = await InvestorService.updateInvestorFields(investor.id, patch, organizationId);
      if (updated) {
        onInvestorUpdated(updated);
      }
      setEditingSection(null);
      toast.success('Investor section saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save investor details');
    } finally {
      setSavingOverview(false);
    }
  };

  const updateNetWorthRow = (index: number, key: 'label' | 'amount' | 'notes', value: string) => {
    setDraft((prev) => ({
      ...prev,
      netWorthRequirementRows: (prev.netWorthRequirementRows ?? []).map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row;
        }
        if (key === 'amount') {
          const cleaned = value.replace(/[$,]/g, '').trim();
          const parsed = cleaned ? Number(cleaned) : null;
          return { ...row, amount: parsed !== null && Number.isFinite(parsed) ? parsed : null };
        }
        return { ...row, [key]: value };
      })
    }));
  };

  const addNetWorthRow = () => {
    setDraft((prev) => ({
      ...prev,
      netWorthRequirementRows: [...(prev.netWorthRequirementRows ?? []), { label: '', amount: null, notes: '' }]
    }));
  };

  const removeNetWorthRow = (index: number) => {
    setDraft((prev) => ({
      ...prev,
      netWorthRequirementRows: (prev.netWorthRequirementRows ?? []).filter((_, rowIndex) => rowIndex !== index)
    }));
  };

  const toggleDraftProduct = (channel: 'w' | 'c', product: string) => {
    const key = channel === 'w' ? 'wholesaleProducts' : 'correspondentProducts';
    setDraft((prev) => {
      const next = new Set(prev[key] ?? []);
      const removing = next.has(product);
      if (removing) {
        next.delete(product);
      } else {
        next.add(product);
      }
      // Item 6: a product removed from correspondent products is also pruned from delegation.
      const delegationPatch =
        channel === 'c' && removing && prev.delegation
          ? {
              delegation: {
                delegated: prev.delegation.delegated.filter((p) => p !== product),
                nonDelegated: prev.delegation.nonDelegated.filter((p) => p !== product)
              }
            }
          : {};
      return { ...prev, [key]: Array.from(next), ...delegationPatch };
    });
  };

  // Item 6: toggle a product under a delegation level; mutually exclusive across levels.
  const toggleDelegation = (level: 'delegated' | 'nonDelegated', product: string) => {
    setDraft((prev) => {
      const current = prev.delegation ?? { delegated: [], nonDelegated: [] };
      const other = level === 'delegated' ? 'nonDelegated' : 'delegated';
      const levelSet = new Set(current[level]);
      const otherSet = new Set(current[other]);
      if (levelSet.has(product)) {
        levelSet.delete(product);
      } else {
        levelSet.add(product);
        otherSet.delete(product);
      }
      const nextDelegated = level === 'delegated' ? Array.from(levelSet) : Array.from(otherSet);
      const nextNonDelegated = level === 'nonDelegated' ? Array.from(levelSet) : Array.from(otherSet);
      return { ...prev, delegation: { delegated: nextDelegated, nonDelegated: nextNonDelegated } };
    });
  };

  const sectionActions = (section: string, keys: Array<keyof Investor>) => {
    if (!canManage) {
      return null;
    }
    const editing = editingSection === section;
    return (
      <div className="lw-section-actions">
        {editing ? (
          <>
            <button type="button" className="lw-btn lw-btn-primary sm" onClick={() => void saveSectionEdit(keys)} disabled={savingOverview}>
              Save
            </button>
            <button type="button" className="lw-btn lw-btn-ghost sm" onClick={cancelSectionEdit} disabled={savingOverview}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="lw-btn lw-btn-secondary sm" onClick={() => setEditingSection(section)} disabled={Boolean(editingSection)}>
            <Pencil className="h-4 w-4" /> Edit
          </button>
        )}
      </div>
    );
  };

  const refreshTemplates = async () => {
    const templates = await investorEncompassTemplateService.getTemplatesByInvestorId(investor.id);
    setLinkedTemplates(templates);
  };

  const handleUnlinkTemplate = async (templateId: string) => {
    if (!canManage || unlinkingTemplateIds.has(templateId)) {
return;
}

    setUnlinkingTemplateIds((prev) => new Set(prev).add(templateId));
    try {
      await investorEncompassTemplateService.unlinkTemplate(templateId);
      setLinkedTemplates((prev) => prev.filter((template) => template.id !== templateId));
      toast.success('Template unlinked successfully');
    } catch (err) {
      log.error('Failed to unlink template', { templateId }, err as Error);
      toast.error(err instanceof Error ? err.message : 'Failed to unlink template');
    } finally {
      setUnlinkingTemplateIds((prev) => {
        const next = new Set(prev);
        next.delete(templateId);
        return next;
      });
    }
  };

  const handleLinkSuccess = async () => {
    try {
      await refreshTemplates();
    } catch (err) {
      log.error('Failed to refresh templates after link', { investorId: investor.id }, err as Error);
    } finally {
      setShowLinkDialog(false);
    }
  };

  const handleSyncTemplate = async (template: InvestorEncompassTemplate) => {
    if (!canManage) {
return;
}

    setSyncingTemplateId(template.id);
    try {
      await investorEncompassTemplateService.syncTemplates({
        investorId: investor.id,
        encompassTemplateId: template.encompassTemplateId,
        forceRefresh: true
      });
      await refreshTemplates();
      toast.success('Template synced successfully');
    } catch (err) {
      log.error('Failed to sync template', { templateId: template.id }, err as Error);
      toast.error(err instanceof Error ? err.message : 'Failed to sync template');
    } finally {
      setSyncingTemplateId(null);
    }
  };

  const tabDefs: Array<{ value: string; label: string; icon: React.ReactNode }> = [
    { value: 'overview', label: 'Overview', icon: <LayoutGrid className="h-4 w-4" /> },
    { value: 'documents', label: 'Documents', icon: <FileText className="h-4 w-4" /> },
    { value: 'processing', label: 'Processing', icon: <Settings2 className="h-4 w-4" /> },
    ...(correspondentApproved ? [{ value: 'closing', label: 'Closing', icon: <KeyRound className="h-4 w-4" /> }] : []),
    { value: 'templates', label: 'Investor Templates', icon: <LinkIcon className="h-4 w-4" /> },
    { value: 'activity', label: 'Activity', icon: <Clock className="h-4 w-4" /> }
  ];

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        hideClose
        onOpenAutoFocus={(event) => event.preventDefault()}
        className={`${LW_THEME_CLASS} lw-slideover w-full p-0`}
      >
        <SheetTitle className="sr-only">
          {investor.investor ? `${investor.investor} investor details` : 'Investor details'}
        </SheetTitle>
        {/* Header */}
        <div className="lw-slideover-header">
          <div className="lw-profile-name">
            <button className="lw-icon-btn" onClick={() => handleOpenChange(false)} aria-label="Close investor details">
              <X className="h-4 w-4" />
            </button>
            <span className="lw-avatar xl" style={logoColor ? { background: logoColor } : undefined}>
              {getInvestorAvatarLabel(investor.investor)}
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="lw-profile-name-text truncate">{investor.investor}</div>
              <div className="lw-profile-meta">
                {investor.website && (
                  <a
                    href={formatUrl(investor.website)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
                  >
                    <Globe size={11} />
                    {stripProtocol(investor.website)}
                  </a>
                )}
                {investor.updatedAt && <span>Updated {formatHeaderTimeAgo(investor.updatedAt)}{investor.updatedByName ? ` by ${investor.updatedByName}` : ''}</span>}
              </div>
            </div>
          </div>
          <div className="lw-channel-tags">
            {investor.canDoWholesale && (
              <span className="lw-channel-tag w">✓ Wholesale</span>
            )}
            {investor.canGoCorrespondent && (
              <span className="lw-channel-tag c">✓ Correspondent</span>
            )}
          </div>
        </div>

        <Tabs activationMode="manual" value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
          {/* Tab bar */}
          <TabsList className="lw-tabs h-auto justify-start rounded-none bg-transparent" aria-label="Investor details">
            {tabDefs.map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="lw-tab rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                {t.icon} {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="lw-slideover-body">
            <TabsContent value="overview" className="mt-0 space-y-4 lw-detail-stack">
              <InvestorRelationshipSections
                investor={investor}
                draft={draft}
                editingSection={editingSection}
                hasWholesale={hasWholesale}
                hasCorrespondent={hasCorrespondent}
                updateDraft={updateDraft}
                sectionActions={sectionActions}
                toggleDraftProduct={toggleDraftProduct}
                websiteLabel={stripProtocol(investor.website)}
              />
              <InvestorPolicySections
                investor={investor}
                draft={draft}
                editingSection={editingSection}
                hasWholesale={hasWholesale}
                hasCorrespondent={hasCorrespondent}
                updateDraft={updateDraft}
                sectionActions={sectionActions}
                isStrictAdmin={isStrictAdmin}
                netWorthRows={netWorthRows}
                toggleDelegation={toggleDelegation}
                updateNetWorthRow={updateNetWorthRow}
                addNetWorthRow={addNetWorthRow}
                removeNetWorthRow={removeNetWorthRow}
              />
            </TabsContent>

            <TabsContent value="documents" className="mt-0">
              <DocumentsTab
                investorId={investor.id}
                organizationId={organizationId}
                investor={investor}
                canManage={canManage}
                onUploadPendingChange={setUploadPending}
              />
            </TabsContent>

            <TabsContent value="processing" className="mt-0">
              <CredentialsTab
                investor={investor}
                organizationId={organizationId}
                variant="processing"
                canManage={canManage}
                onInvestorUpdated={onInvestorUpdated}
              />
            </TabsContent>

            {correspondentApproved && (
              <TabsContent value="closing" className="mt-0">
                <CredentialsTab
                  investor={investor}
                  organizationId={organizationId}
                  variant="closing"
                  canManage={canManage}
                  onInvestorUpdated={onInvestorUpdated}
                />
              </TabsContent>
            )}

            <TabsContent value="templates" className="mt-0">
              <InvestorTemplatesTab
                templates={linkedTemplates}
                servicerSelectionMode={servicerSelectionMode}
                onServicerSelectionModeChanged={setServicerSelectionMode}
                investorId={investor.id}
                onLinkClick={() => setShowLinkDialog(true)}
                onUnlink={handleUnlinkTemplate}
                onSync={handleSyncTemplate}
                unlinkingTemplateIds={unlinkingTemplateIds}
                syncingTemplateId={syncingTemplateId}
                isLoading={isLoadingTemplates}
                canManage={canManage}
                onTemplatesChanged={refreshTemplates}
              />
            </TabsContent>

            <TabsContent value="activity" className="mt-0">
              {activities.length > 0 ? (
                <div className="space-y-2">
                  {activities.map((activity) => (
                    <div key={activity.id} className="lw-activity-row" style={{ background: 'var(--lw-bg-card)', border: '1px solid var(--lw-border)' }}>
                      <div className="lw-activity-marker" aria-hidden="true">{getActivityIcon(activity.type)}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${getActivityColor(activity.type)}`}>{activity.action}</span>
                          <span className="text-xs text-muted-foreground">{formatTimeAgo(activity.timestamp)}</span>
                        </div>
                        {activity.details && <p className="text-xs text-muted-foreground">{activity.details}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No recent activity.</p>
              )}
            </TabsContent>
          </div>
        </Tabs>
        {canManage && (
          <LinkEncompassTemplateDialog
            open={showLinkDialog}
            servicerSelectionMode={servicerSelectionMode}
            onOpenChange={setShowLinkDialog}
            investorId={investor.id}
            onLinkSuccess={() => void handleLinkSuccess()}
          />
        )}
      </SheetContent>
    </Sheet>
  );
};

export default InvestorSlideOver;
